import {
  useEffect,
  useState,
  useRef,
  type MutableRefObject,
  type ChangeEvent,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { IoMdArrowDropright } from 'react-icons/io';
import { RxHamburgerMenu } from 'react-icons/rx';
import { IoIosSearch, IoIosClose } from 'react-icons/io';
import { IoLogOutOutline, IoHelpCircleOutline } from 'react-icons/io5';

import { FETCH_PRINCIPAL_WITH_ID_SUCCESS, fetchPrincipals } from '../../redux/principalActions';
import { fetchProgramStats } from '../../redux/programActions';

type RootState = any;

interface PrincipalPageProps {
  setAuth: React.Dispatch<React.SetStateAction<boolean>>;
}

function PrincipalPage({ setAuth }: PrincipalPageProps) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { principal_list, loading } = useSelector((state: RootState) => state.principal);
  const { stats, statsLoading, statsError } = useSelector((state: RootState) => state.program);

  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearchVisible, setSearchVisible] = useState(false);
  const sidebarRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // @ts-expect-error thunk
    dispatch(fetchPrincipals());
    // @ts-expect-error thunk
    dispatch(fetchProgramStats());
  }, [dispatch]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target as Node)
      ) {
        setSidebarOpen(false);
      }
      const target = event.target as HTMLElement | null;
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node) &&
        !target?.closest('button[aria-label="Toggle search"]')
      ) {
        setSearchVisible(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleClick = async (id: number, name: string) => {
    try {
      dispatch({
        type: FETCH_PRINCIPAL_WITH_ID_SUCCESS,
        payload: {
          name,
          principalId: id,
        },
      });
      navigate(`/principal/${id}`);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

  const toggleSidebar = () => {
    setSidebarOpen((prev) => !prev);
  };

  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    setAuth(false);
    navigate('/');
  };

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const filteredPrincipals =
    principal_list?.principals?.rows?.filter(
      (item: any) => item.name.toLowerCase().includes(searchTerm.toLowerCase()),
    ) ?? [];

  const toggleSearchVisibility = () => {
    setSearchVisible((prev) => !prev);
    if (isSearchVisible) {
      setSearchTerm('');
    }
  };

  return (
    <div className="h-screen bg-white overflow-auto">
      {loading && (
        <div className="fixed inset-0 bg-white/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="w-8 h-8 border-4 border-gray-300 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {principal_list && (
        <div className="h-full flex flex-col">
          <div className="bg-white border-b shadow-sm sticky top-0 z-30">
            <div className="max-w-[1200px] mx-auto">
              <div className="px-4 sm:px-6">
                <div className="h-16 flex items-center">
                  <button
                    type="button"
                    onClick={toggleSidebar}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                  >
                    <RxHamburgerMenu className="w-5 h-5 text-gray-600" />
                  </button>
                  <div className="flex-1 flex justify-center">
                    {!isSearchVisible && (
                      <h1 className="text-lg font-semibold text-gray-900">Principals</h1>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <div
                      ref={searchRef as MutableRefObject<HTMLDivElement | null>}
                      className={`overflow-hidden transition-all duration-300 ease-in-out flex items-center ${
                        isSearchVisible ? 'flex-1 max-w-xs' : 'w-0'
                      }`}
                    >
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={handleSearchChange}
                        placeholder="Search principals..."
                        disabled={!isSearchVisible}
                        autoFocus={isSearchVisible}
                        className={`w-full h-10 px-4 rounded-xl border border-gray-200 focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 transition-all duration-300 bg-gray-50 ${
                          isSearchVisible
                            ? 'opacity-100 translate-x-0 pointer-events-auto'
                            : 'opacity-0 -translate-x-full pointer-events-none'
                        }`}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={toggleSearchVisibility}
                      aria-label="Toggle search"
                      className="p-2.5 hover:bg-gray-100 active:bg-gray-200 rounded-xl transition-colors"
                    >
                      <IoIosSearch className="w-5 h-5 text-gray-600" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 pt-6">
            <div className="max-w-5xl mx-auto px-4 sm:px-6">
              <div className="grid grid-cols-2 gap-4">
                {statsLoading ? (
                  <div className="col-span-4 flex justify-center items-center h-32">
                    <div className="w-8 h-8 border-4 border-gray-300 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : statsError ? (
                  <div className="col-span-4 text-center text-red-500">{statsError}</div>
                ) : stats ? (
                  [
                    { value: stats.totalOutlets, label: 'Total Outlets', color: 'text-brand', bg: 'bg-brand/5' },
                    { value: stats.activePrograms, label: 'Active Programs', color: 'text-emerald-600', bg: 'bg-emerald-50' },
                    { value: stats.verifiedQrs, label: 'Verified QRs', color: 'text-amber-600', bg: 'bg-amber-50' },
                    { value: stats.pendingQrs, label: 'Pending', color: 'text-slate-700', bg: 'bg-slate-100' },
                  ].map((item) => (
                    <div
                      key={item.label}
                      className={`w-full ${item.bg} flex flex-col items-center justify-center px-6 py-5 rounded-xl border border-transparent shadow-soft transition-all hover:shadow-soft-lg`}
                    >
                      <div className={`text-2xl font-bold ${item.color}`}>{item.value}</div>
                      <div className="mt-1 text-gray-600 text-sm font-medium">{item.label}</div>
                    </div>
                  ))
                ) : null}
              </div>
            </div>
          </div>

          <div
            ref={sidebarRef as MutableRefObject<HTMLDivElement | null>}
            className={`fixed inset-y-0 left-0 z-40 w-72 bg-white shadow-xl transform transition-transform duration-300 ease-in-out ${
              isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
            }`}
          >
            <div className="h-16 flex items-center justify-between px-4 border-b">
              <h2 className="text-lg font-semibold text-gray-900">Menu</h2>
              <button
                type="button"
                onClick={toggleSidebar}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <IoIosClose className="w-6 h-6 text-gray-600" />
              </button>
            </div>

            <nav className="p-4 space-y-3">
              <button
                type="button"
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-xl transition-colors"
              >
                <IoLogOutOutline className="w-5 h-5 text-gray-500" />
                <span>Logout</span>
              </button>

              <div className="bg-gray-50 rounded-xl p-4">
                <div className="flex items-center gap-3 px-2 py-2 text-sm font-medium text-gray-700">
                  <IoHelpCircleOutline className="w-5 h-5 text-gray-500" />
                  <span>Help Center</span>
                </div>
                <a
                  href="tel:+919089089089"
                  className="block px-4 py-2 text-sm text-brand font-medium hover:text-brand-dark"
                >
                  +91 9089089089
                </a>
              </div>
            </nav>
          </div>

          <div className="flex-1 bg-gray-50/80">
            <div className="p-4 sm:p-6">
              <div className="max-w-5xl mx-auto space-y-3">
                {filteredPrincipals.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
                    <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                      <IoIosSearch className="w-8 h-8 text-gray-400" />
                    </div>
                    <p className="text-gray-600 font-medium">No principals found</p>
                    <p className="text-sm text-gray-500 mt-1">
                      {searchTerm ? 'Try a different search term' : 'No principals to display'}
                    </p>
                  </div>
                ) : (
                  filteredPrincipals.map((item: any, index: number) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => handleClick(item.principalId, item.name)}
                      className="w-full flex items-center justify-between px-6 py-4 text-left bg-white hover:bg-white rounded-xl transition-all shadow-soft hover:shadow-card-hover border border-gray-100 active:scale-[0.99]"
                    >
                      <span className="text-gray-900 font-medium">{item.name}</span>
                      <IoMdArrowDropright className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PrincipalPage;

